import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/appState/AppState';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { EventsCollectionList } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList';
import {
    EventsFilterGroupComputeds,
    EventsFilterGroupFlagState,
} from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterGroupComputeds';
import { EventsFilterGroup } from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterGroup';
import { FilterKindType } from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterType';
import { FiltersSkinType } from 'src/domains/layouts/webview/components/filters/FiltersSkin';
import { EventsTableLoader } from 'src/domains/sportsbook/webview/components/eventsTable/InPlayLoader';
import { EventsTableMarketsLoader } from 'src/domains/sportsbook/webview/components/loaders/EventsTableMarketsLoader';
import { EventsTableHeaderStyle } from 'src/domains/sportsbook/webview/components/eventsTable/EventsMainSectionWrapper';
import { FiltersStyle } from 'src/domains/sportsbook/webview/components/eventsTable/EventsTable.style';
import { EventMainSection } from 'src/domains/sportsbook/webview/components/eventsTable/EventsMainSectionWrapper.style';
import { EventsListUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsListUnified';
import { EventsTableInfo } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX.style';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { Collapse } from 'src/ui/components/collapse/sports/01/Collapse';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SpecialSportsListState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportListState';

interface HomepageUnifiedEventsSectionPropsType {
    /** Homepage slice (`listInPlay`, upcoming collection, …). */
    collection: EventsCollectionList;
    className?: string;
    /** Mirrors EventsTable `data-test` on root section. */
    dataTest: string;
    filters: Array<FilterKindType>;
    filterVersion: FiltersSkinType;
    hideWhenEmpty: boolean;
    isLoading: boolean;
    showPriceLegend: boolean;
    title: React.ReactNode;
}

/** Unified-events list for homepage in-play / upcoming when `unifiedSportEventsTable` is on. */
export const HomepageUnifiedEventsSection = observer(
    'HomepageUnifiedEventsSection',
    (props: HomepageUnifiedEventsSectionPropsType): JSX.Element | null => {
        const {
            collection,
            className,
            dataTest,
            filters,
            filterVersion,
            hideWhenEmpty,
            isLoading,
            showPriceLegend,
            title,
        } = props;

        const common = useCommon();
        const configState = ConfigComponents.get(common).config;
        const { sportsbookVariant } = configState.componentVariants;
        const { appSportsBookState } = useAppStateContext();

        const [flag] = useState(() => new EventsFilterGroupFlagState());

        const eventsFilter = new EventsFilterGroupComputeds(common, () => collection.getQuery() ?? null, null, flag);

        if (isLoading === true) {
            return <EventsTableLoader />;
        }

        if (collection.length === 0) {
            if (hideWhenEmpty === true) {
                return null;
            }

            return (
                <EventMainSection
                    data-test={dataTest}
                    className={className}
                >
                    <EventsTableInfo>
                        <I18n
                            langKey='events.table.empty'
                            defaultText="Sorry, we haven't found any events with such criteria."
                        />
                    </EventsTableInfo>
                </EventMainSection>
            );
        }

        const filteredQuery = eventsFilter.filter;

        if (filteredQuery === null) {
            return <EventsTableMarketsLoader />;
        }

        const anchorSportId = filteredQuery.value.sports[0]?.id ?? 'football';
        const eventsListStateAnchor = appSportsBookState.unifiedSportEventsListState.get(anchorSportId).get(null);

        const isSpecialNotLoading = SpecialSportsListState.get(common).isLoading === false;

        return (
            <EventMainSection
                data-test={dataTest}
                className={className}
            >
                <Collapse
                    dataTest='collapse-main-section'
                    handleToggle={eventsFilter.flag.onToggle}
                    isOpen={eventsFilter.flag.isOpen}
                    title={title}
                />
                {eventsFilter.flag.isOpen === true ? (
                    <>
                        {isSpecialNotLoading === true && filters.length > 0 ? (
                            <FiltersStyle
                                headerStyle={EventsTableHeaderStyle.SOLID}
                                isCompetitionPage={false}
                                variant={sportsbookVariant}
                            >
                                <EventsFilterGroup
                                    skin={filterVersion}
                                    eventsFilter={eventsFilter}
                                    filters={filters}
                                    key='header-filters-unified-homepage'
                                />
                            </FiltersStyle>
                        ) : null}

                        <EventsListUnified
                            allGroupsOpen={true}
                            eventsCollectionQueryOverride={filteredQuery}
                            eventsListState={eventsListStateAnchor}
                            groupingSpecialSports={null}
                            preferCompetitionSelectionsHeadersOnly={true}
                            showHeaderSport={true}
                            sportId={anchorSportId}
                        />

                        {showPriceLegend === true ? <PriceChangeLegend isRace={false} /> : null}
                    </>
                ) : null}
            </EventMainSection>
        );
    }
);
